/**
 * PHASE-A-IMPLEMENTATION-000001 G01 — Order Items founder workspace.
 * SoT: MarketSync domain=order_items (SP-API getOrderItems).
 * Join chain: Orders (order id) ↓ Order Items ↓ Products / Pricing (ASIN, SKU) ↓ Finance (order id).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  amazonConnectApi,
  type OrderItemPage as OrderItemPageResult,
  type OrderItemRow,
  type SellerConnection,
} from '../../api/amazonconnect';
import {
  PAGE_SIZE_OPTIONS,
  PlatformDataGrid,
  type DataGridColumn,
  type DataGridMeta,
  type PageSizeOption,
} from '../../components/platform/PlatformDataGrid';
import { getMembershipId, getWorkspaceId } from '../../lib/auth-storage';
import { FounderCommerceShell, commerceDeepLink } from './workspace/FounderCommerceShell';
import {
  AsinLink,
  ComingSoonCell,
  OrderLink,
  SkuLink,
  WorkspaceLegend,
} from './workspace/commerceFields';
import { useCommerceDomainHealth } from './workspace/useCommerceDomainHealth';

export function MarketSyncOrderItemsPage() {
  const workspaceKey = getWorkspaceId() ?? 'default';
  const userKey = getMembershipId() ?? 'anon';
  const [params, setParams] = useSearchParams();

  const [connections, setConnections] = useState<SellerConnection[]>([]);
  const [connectionId, setConnectionId] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<PageSizeOption>(25);
  const [q, setQ] = useState('');
  const [qDraft, setQDraft] = useState('');
  const [sortCol, setSortCol] = useState('purchase_date');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [result, setResult] = useState<OrderItemPageResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [latencyMs, setLatencyMs] = useState<number | undefined>();
  const [bootstrapped, setBootstrapped] = useState(false);

  const orderFilter = params.get('order_id') ?? '';

  useEffect(() => {
    void amazonConnectApi
      .listConnections()
      .then((c) => {
        setConnections(c);
        const urlConn = params.get('connection') || '';
        const pick = urlConn && c.some((x) => x.id === urlConn) ? urlConn : c[0]?.id || '';
        setConnectionId((prev) => prev || pick);
      })
      .catch(() => setConnections([]));
  }, [params]);

  useEffect(() => {
    if (bootstrapped) return;
    const uq = params.get('q') || '';
    if (uq) {
      setQ(uq);
      setQDraft(uq);
    }
    setBootstrapped(true);
  }, [params, bootstrapped]);

  const connection = connections.find((c) => c.id === connectionId);
  const health = useCommerceDomainHealth(connectionId, 'order_items');
  const sortParam = order === 'desc' ? `${sortCol}_desc` : sortCol;

  const load = useCallback(async () => {
    if (!connectionId) {
      setLoading(false);
      setResult(null);
      return;
    }
    setLoading(true);
    setError(null);
    const t0 = performance.now();
    try {
      const res = await amazonConnectApi.listOrderItemsPage(connectionId, {
        page,
        page_size: pageSize,
        q: q || undefined,
        sort: sortParam,
        order,
        order_id: orderFilter || undefined,
      });
      setLatencyMs(Math.round(performance.now() - t0));
      setResult(res);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load order items');
      setResult(null);
      setLatencyMs(undefined);
    } finally {
      setLoading(false);
    }
  }, [connectionId, page, pageSize, q, sortParam, order, orderFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  const rows = result?.items ?? [];
  const meta: DataGridMeta | null = result
    ? {
        total_count: result.total_count,
        returned_count: result.returned_count ?? result.count,
        page: result.page,
        page_size: result.page_size,
        pages: result.pages,
        remaining: result.remaining,
        has_more: result.has_more,
        execution_time_ms: result.execution_time_ms ?? latencyMs,
        source: result.source,
        data_source_badge: result.source || 'MarketSync · order_items',
        last_sync: health.lastSync || connection?.last_sync_at || rows[0]?.last_sync,
        amazon_count: health.amazonCount,
        realtime: health.realtimeEligible,
      }
    : null;

  const columns: DataGridColumn<OrderItemRow>[] = useMemo(
    () => [
      {
        id: 'amazon_order_id',
        header: 'Amazon Order ID',
        width: 160,
        sortable: true,
        stickyLeft: 0,
        render: (r) => <OrderLink orderId={r.amazon_order_id} connectionId={connectionId} />,
        exportValue: (r) => r.amazon_order_id,
      },
      {
        id: 'order_item_id',
        header: 'Line ID',
        width: 130,
        render: (r) => <span className="font-mono text-[11px]">{r.order_item_id}</span>,
        exportValue: (r) => r.order_item_id,
      },
      {
        id: 'asin',
        header: 'ASIN',
        width: 120,
        sortable: true,
        render: (r) =>
          r.asin ? (
            <AsinLink asin={r.asin} marketplaceId={r.marketplace_id} connectionId={connectionId} />
          ) : (
            <ComingSoonCell />
          ),
        exportValue: (r) => r.asin ?? '',
      },
      {
        id: 'sku',
        header: 'SKU',
        width: 130,
        sortable: true,
        render: (r) =>
          r.sku ? <SkuLink sku={r.sku} connectionId={connectionId} /> : <ComingSoonCell />,
        exportValue: (r) => r.sku ?? '',
      },
      {
        id: 'title',
        header: 'Title',
        width: 240,
        render: (r) => (
          <span className="block truncate" title={r.title || undefined}>
            {r.title || '—'}
          </span>
        ),
        exportValue: (r) => r.title ?? '',
      },
      {
        id: 'quantity_ordered',
        header: 'Qty',
        width: 70,
        sortable: true,
        render: (r) => <span className="font-mono tabular-nums">{r.quantity_ordered}</span>,
        exportValue: (r) => String(r.quantity_ordered),
      },
      {
        id: 'quantity_shipped',
        header: 'Shipped',
        width: 80,
        render: (r) => <span className="font-mono tabular-nums">{r.quantity_shipped}</span>,
        exportValue: (r) => String(r.quantity_shipped),
      },
      {
        id: 'item_price',
        header: 'Item Price',
        width: 110,
        render: (r) =>
          r.item_price ? (
            <span className="font-mono tabular-nums" title="Amazon ItemPrice — extended line amount">
              {r.item_price}
            </span>
          ) : (
            <ComingSoonCell />
          ),
        exportValue: (r) => r.item_price ?? '',
      },
      {
        id: 'unit_price',
        header: 'Unit Price',
        width: 110,
        render: (r) =>
          r.unit_price ? (
            <span
              className="font-mono tabular-nums text-indigo-900 dark:text-indigo-300"
              title="Calculated: ItemPrice ÷ QuantityOrdered (LOGIC-BIBLE). Not an Amazon field."
            >
              {r.unit_price}
            </span>
          ) : (
            <ComingSoonCell />
          ),
        exportValue: (r) => r.unit_price ?? '',
      },
      {
        id: 'item_tax',
        header: 'Tax',
        width: 90,
        render: (r) =>
          r.item_tax ? (
            <span className="font-mono tabular-nums">{r.item_tax}</span>
          ) : (
            <ComingSoonCell />
          ),
        exportValue: (r) => r.item_tax ?? '',
      },
      {
        id: 'promotion_discount',
        header: 'Promo',
        width: 90,
        render: (r) =>
          r.promotion_discount ? (
            <span className="font-mono tabular-nums">{r.promotion_discount}</span>
          ) : (
            <ComingSoonCell />
          ),
        exportValue: (r) => r.promotion_discount ?? '',
      },
      {
        id: 'currency',
        header: 'CCY',
        width: 56,
        render: (r) => r.currency ?? '—',
        exportValue: (r) => r.currency ?? '',
      },
      {
        id: 'purchase_date',
        header: 'Purchase (IST)',
        width: 150,
        sortable: true,
        render: (r) => (
          <span className="font-mono text-[10px]" title={r.purchase_date ? `UTC ${r.purchase_date}` : undefined}>
            {r.purchase_date_ist ?? r.purchase_date ?? '—'}
          </span>
        ),
        exportValue: (r) => r.purchase_date_ist ?? r.purchase_date ?? '',
      },
      {
        id: 'order_status',
        header: 'Status',
        width: 110,
        render: (r) => (
          <span className="rounded-md bg-slate-100 px-1.5 py-0.5 text-[11px] font-medium dark:bg-slate-800">
            {r.order_status ?? '—'}
          </span>
        ),
        exportValue: (r) => r.order_status ?? '',
      },
      {
        id: 'fulfilment',
        header: 'Fulfilment',
        width: 90,
        render: (r) => r.fulfilment ?? '—',
        exportValue: (r) => r.fulfilment ?? '',
      },
      {
        id: 'finance',
        header: 'Finance',
        width: 90,
        render: (r) => (
          <Link
            to={commerceDeepLink('finance', {
              connection: connectionId || undefined,
              q: r.amazon_order_id,
            })}
            className="text-teal-800 underline-offset-2 hover:underline dark:text-teal-300"
            title="Open this order's financial events"
          >
            Events
          </Link>
        ),
        exportValue: (r) => r.amazon_order_id,
      },
      {
        id: 'marketplace',
        header: 'Marketplace',
        width: 110,
        render: (r) => <span className="font-mono">{r.marketplace_id ?? '—'}</span>,
        exportValue: (r) => r.marketplace_id ?? '',
      },
    ],
    [connectionId],
  );

  const clearOrderFilter = () => {
    const next = new URLSearchParams(params);
    next.delete('order_id');
    setParams(next, { replace: true });
    setPage(1);
  };

  return (
    <FounderCommerceShell
      domain="order_items"
      title="Order Items"
      subtitle="Amazon order lines from getOrderItems. Every field is verbatim Amazon except Unit Price, which is ItemPrice ÷ quantity. An order whose line fetch failed shows no rows rather than a false zero."
      entryKey="Order ID + Line ID"
      connection={connection}
      domainBadge={health.quality || 'order_items'}
      legend={<WorkspaceLegend />}
    >
      {orderFilter ? (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-teal-200 bg-teal-50 px-3 py-2 text-sm dark:border-teal-800 dark:bg-teal-950">
          <span className="text-teal-900 dark:text-teal-200">
            Showing lines for order{' '}
            <span className="font-mono font-medium">{orderFilter}</span>
          </span>
          <button
            type="button"
            onClick={clearOrderFilter}
            className="min-h-8 rounded-md bg-white px-2 text-xs font-medium text-teal-900 transition-colors duration-200 hover:bg-teal-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 dark:bg-slate-900 dark:text-teal-200"
          >
            Show all orders
          </button>
        </div>
      ) : null}

      <PlatformDataGrid
        gridId="order-items-v1"
        workspaceKey={workspaceKey}
        userKey={userKey}
        columns={columns}
        rows={rows}
        rowKey={(r) => `${r.amazon_order_id}|${r.order_item_id}`}
        meta={meta}
        loading={loading}
        error={error}
        emptyMessage="No order lines. Run the Order Items sync from Connect (it follows Orders), then refresh."
        emptyAction={
          <Link to="/amazon/connect" className="rounded-lg bg-slate-900 px-4 py-2 text-sm text-white">
            Open Connect
          </Link>
        }
        page={page}
        pageSize={pageSize}
        sort={sortCol}
        order={order}
        searchDraft={qDraft}
        searchApplied={q || undefined}
        onSearchDraftChange={setQDraft}
        onSearch={() => {
          setQ(qDraft.trim());
          setPage(1);
        }}
        onPageChange={setPage}
        onPageSizeChange={(n) => {
          if (PAGE_SIZE_OPTIONS.includes(n)) {
            setPageSize(n);
            setPage(1);
          }
        }}
        onSortChange={(col, ord) => {
          setSortCol(col);
          setOrder(ord);
          setPage(1);
        }}
        onRefresh={() => void load()}
        selectable
        selectedKeys={selected}
        onSelectionChange={setSelected}
        density="compact"
        toolbarExtra={
          <label className="text-sm">
            <span className="text-xs font-medium text-slate-500">Connection</span>
            <select
              className="mt-1 block min-h-11 min-w-[16rem] rounded-lg border border-slate-300 px-2 dark:border-slate-600 dark:bg-slate-900"
              value={connectionId}
              onChange={(e) => {
                setConnectionId(e.target.value);
                setPage(1);
                setSelected(new Set());
              }}
            >
              {connections.length === 0 && <option value="">No connections</option>}
              {connections.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.seller_ref || c.id.slice(0, 8)} · {c.status}
                </option>
              ))}
            </select>
          </label>
        }
      />
    </FounderCommerceShell>
  );
}
